import { FC, ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

type OverlayType = 'success' | 'cancel'

interface ActionsContextValue {
    set: (type: OverlayType, message: string) => void
    unset: () => void
    setSuccess: (message: string) => void
    setCancel: (message: string) => void
}

type ContextValue = {
    type: OverlayType
    message: string
} | null

const Context = createContext<ContextValue>(null)

const ActionsContext = createContext<ActionsContextValue>({
    set: () => {},
    unset: () => {},
    setSuccess: () => {},
    setCancel: () => {},
})

interface OverlayProviderProps {
    children: ReactNode
}

const OverlayProvider: FC<OverlayProviderProps> = ({ children }) => {
    const [state, setState] = useState<ContextValue>(null)

    const set = useCallback((type: OverlayType, message: string) => {
        setState({ type, message })
    }, [])

    const unset = useCallback(() => setState(null), [])

    const setSuccess = useCallback((message: string) => set('success', message), [set])
    const setCancel = useCallback((message: string) => set('cancel', message), [set])

    const actions = useMemo(
        () => ({ set, unset, setSuccess, setCancel }),
        [set, unset, setSuccess, setCancel]
    )

    return (
        <Context.Provider value={state}>
            <ActionsContext.Provider value={actions}>{children}</ActionsContext.Provider>
        </Context.Provider>
    )
}

export default OverlayProvider

export const useOverlay = (): ContextValue => useContext(Context)
export const useOverlayActions = (): ActionsContextValue => useContext(ActionsContext)
